import { Card, CardContent, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Função para obter cor baseada no nivelAlerta
const getCorPorNivelAlerta = (nivelAlerta, theme) => {
  // nivelAlerta: 0 = Normal, 1 = Atenção, 2 = SegundaAfericao, 3 = Crítico
  switch (nivelAlerta) {
    case 0: // Normal - verde
      return {
        border: theme.palette.success.main,
        background: 'rgba(16, 185, 129, 0.1)',
      };
    case 1: // Atenção - amarelo
      return {
        border: theme.palette.warning.main,
        background: 'rgba(251, 191, 36, 0.1)',
      };
    case 2: // SegundaAfericao - vermelho
      return {
        border: theme.palette.error.main,
        background: 'rgba(239, 68, 68, 0.1)',
      };
    case 3: // Crítico - preto
      return {
        border: '#000000',
        background: 'rgba(0, 0, 0, 0.1)',
      };
    default: // Normal (padrão) - verde
      return {
        border: theme.palette.success.main,
        background: 'rgba(16, 185, 129, 0.1)',
      };
  }
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'realizado' && prop !== 'nivelAlerta',
})(({ theme, realizado, nivelAlerta }) => {
  const cores = realizado ? getCorPorNivelAlerta(nivelAlerta, theme) : {
    border: 'rgba(0, 0, 0, 0.12)',
    background: theme.palette.background.paper,
  };

  return {
    position: 'relative',
    height: '120px',
    width: '100%',
    minWidth: '70px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease-in-out',
    border: `2px solid ${cores.border}`,
    backgroundColor: cores.background,
    borderRadius: '14px',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    overflow: 'hidden',
    boxSizing: 'border-box',
    '&:hover': {
      transform: realizado ? 'none' : 'translateY(-2px)',
      boxShadow: realizado 
        ? '0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.06)'
        : '0px 4px 6px -1px rgba(0, 0, 0, 0.08), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    '&:active': {
      transform: realizado ? 'none' : 'translateY(0) scale(0.98)',
    },
  };
});

export function BicoButton({ bico, realizado, valor, nivelAlerta, onClick }) {
  const tipoCombustivel = bico.tipoCombustivel || bico.TipoCombustivel || {};
  const apelidoCombustivel = tipoCombustivel.apelido || tipoCombustivel.Apelido || tipoCombustivel.nome || tipoCombustivel.Nome || 'N/A';
  const numeroBico = bico.numero || bico.Numero || bico.id;
  
  // Formatar número do bico como B01, B02, etc.
  const numeroFormatado = `B${String(numeroBico).padStart(2, '0')}`;
  
  // Normalizar nivelAlerta (pode ser null, undefined, ou número)
  const nivelAlertaNormalizado = nivelAlerta ?? null;

  return (
    <motion.div
      whileHover={!realizado ? { scale: 1.02 } : {}}
      whileTap={!realizado ? { scale: 0.98 } : {}}
      style={{ width: '100%' }}
    >
      <Box
        component="div"
        onClick={onClick}
        sx={{
          p: 0,
          textTransform: 'none',
          width: '100%',
          cursor: 'pointer',
        }}
      >
        <StyledCard realizado={realizado} nivelAlerta={nivelAlertaNormalizado} sx={{ width: '100%' }}>
          <CardContent 
            sx={{ 
              p: { xs: 1.5, sm: 1.5 }, 
              '&:last-child': { pb: { xs: 1.5, sm: 1.5 } },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              variant="h5"
              component="div"
              fontWeight="bold"
              color="text.primary"
              sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' }, 
                textAlign: 'center',
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >
              {numeroFormatado}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              textTransform="uppercase"
              sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.75rem' }, 
                fontWeight: 500, 
                textAlign: 'center',
                lineHeight: 1.2,
                mb: realizado && valor !== undefined ? 0.5 : 0,
                minHeight: realizado && valor !== undefined ? 'auto' : '20px',
              }}
            >
              {apelidoCombustivel}
            </Typography>
            <Box
              sx={{
                minHeight: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.5,
              }}
            >
              {realizado && valor !== undefined ? (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  style={{ textAlign: 'center', width: '100%' }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }, 
                      textAlign: 'center', 
                      lineHeight: 1.2,
                      color: nivelAlertaNormalizado === 0 
                        ? 'success.main' 
                        : nivelAlertaNormalizado === 1
                        ? 'warning.main'
                        : nivelAlertaNormalizado === 2
                        ? 'error.main'
                        : nivelAlertaNormalizado === 3
                        ? '#000000'
                        : 'success.main' // padrão verde
                    }}
                  >
                    {valor >= 0 ? '+' : ''}{Math.round(valor)} ml
                  </Typography>
                </motion.div>
              ) : (
                <Box sx={{ height: '20px' }} />
              )}
            </Box>
          </CardContent>
        </StyledCard>
      </Box>
    </motion.div>
  );
}
